import React, { useContext } from 'react';
import classnames from 'classnames';
import pathfinder from '@fiverr-private/pathfinder';
import { translate, I18n } from '@fiverr-private/i18n-react';
import { Avatar, Button } from '@fiverr-private/fit';
import { SmileyIcon, CalendarIcon } from '@fiverr-private/icons';
import { spacing12, grey_900 } from '@fiverr-private/sass/helpers';
import { TrackAction } from '../../../../lib/mixpanel';
import { useFormatDate } from '../../../../lib/hooks/useFormatDate';
import { useBigQuery } from '../BigQuery';
import { COPIES_PREFIX } from '../constants';
import { MixpanelContext } from '../context/MixpanelContext';
import { DetailsProps } from './types';

import classes from './Details.module.scss';

export const Details = ({
  deliveryId,
  deliveredDate,
  buyerUserName,
  sellerUserName,
  sellerAvatarUrl,
  sellerOneLinerTitle,
  gigSlug,
}: DetailsProps) => {
  const bigQuery = useBigQuery();
  const trackMixpanel = useContext(MixpanelContext);

  const sellerPageHref = pathfinder('seller_page_username', { username: sellerUserName });
  const gigPageHref = pathfinder('gig_page', { username: sellerUserName, slug: gigSlug });
  const sellerInboxHref = `${pathfinder('inbox_show', { username: sellerUserName })}?inspireDeliveryId=${deliveryId}`;

  const deliveredDateStr = useFormatDate(new Date(deliveredDate * 1000));

  const textAboutTheSeller = translate(`${COPIES_PREFIX}.about_the_seller`);
  const textCompletedOn = translate(`${COPIES_PREFIX}.completed_on`, { params: { date: deliveredDateStr } });
  const textSeeGig = translate(`${COPIES_PREFIX}.see_gig`);
  const textContactSeller = translate(`${COPIES_PREFIX}.contact_seller`);

  const onSeeGigClick = () => {
    bigQuery.trackClickOnSeeGig();
    trackMixpanel(TrackAction.ViewGig);
  };

  const onContactSellerClick = () => {
    bigQuery.trackClickOnContactSeller();
    trackMixpanel(TrackAction.ContactSeller);
  };

  const onViewSellerClick = () => {
    bigQuery.trackClickOnSellerName({ sellerUserName });
    trackMixpanel(TrackAction.ViewSeller);
  };

  return (
    <div className={classnames(classes.container, 'm-l-24 m-r-24 m-t-24 m-b-16')}>
      <p className={classnames(classes.title, 'm-b-16 co-text-darker tbody-5 text-bold')}>{textAboutTheSeller}</p>
      <div className={classes.info}>
        <Avatar size={spacing12} username={sellerUserName} src={sellerAvatarUrl} />
        <div className={classnames(classes.seller, 'tbody-6')}>
          <I18n
            k={`${COPIES_PREFIX}.made_by`}
            params={{ seller: sellerUserName }}
            templates={{
              link: (text) => (
                <a
                  className={classnames(classes.href, 'text-bold')}
                  href={sellerPageHref}
                  onClick={onViewSellerClick}
                  target="_blank"
                  rel="noreferrer"
                >
                  {text}
                </a>
              ),
            }}
          />
          {Boolean(sellerOneLinerTitle) && <p>{sellerOneLinerTitle}</p>}
        </div>
        <div className={classnames(classes.infoLines, 'tbody-6')}>
          <p className={classes.infoLine}>
            <SmileyIcon color={grey_900} />
            <span>
              <I18n
                k={`${COPIES_PREFIX}.made_for`}
                params={{ buyer: buyerUserName }}
                templates={{
                  link: (
                    text // no actual href is needed, but need to keep the same styling
                  ) => <span className={classnames(classes.href, 'text-bold')}>{text}</span>,
                }}
              />
            </span>
          </p>
          <p className={classes.infoLine}>
            <CalendarIcon color={grey_900} />
            <span>{textCompletedOn}</span>
          </p>
        </div>
      </div>
      <div className={classnames(classes.buttons, 'm-t-12')}>
        <Button
          href={gigPageHref}
          className={classes.button}
          onClick={onSeeGigClick}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          target="_blank" /* FIT TS error: should accept HTMLAnchorElement props instead of HTMLButtonElement's if `href` is present */
        >
          {textSeeGig}
        </Button>
        <Button
          href={sellerInboxHref}
          className={classes.button}
          onClick={onContactSellerClick}
          color="light-grey"
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          target="_blank" /* FIT TS error: should accept HTMLAnchorElement props instead of HTMLButtonElement's if `href` is present */
        >
          {textContactSeller}
        </Button>
      </div>
    </div>
  );
};
